import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import {
  AppBar,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { MousePointerClickIcon, XIcon } from 'lucide-react';
import { Route, Routes } from 'react-router-dom';

import type {
  Ch_Cma_Listings_Distance_Bool_Exp,
  GetPropertyTypesQuery,
  GetTransactionPropertyQuery,
} from '@graphql/__generated__/graphql';
import { GET_PROPERTY_TYPES } from '@modules/_shared/common/queries';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { TransactionCard } from '@modules/_shared/components/TransactionCard';
import { PropertyTypesProvider } from '@modules/_shared/providers/PropertyTypesProvider';
import { useScopedSearchParams } from '@modules/_shared/utils/navigation';
import ComparableListings from '@modules/comparable-listings/ComparableListings';
import CmaScraperCard from '@modules/reports/cma-reports/step-listing-compare/CmaScraperCard';
import ListingComparableDetails from '@modules/reports/cma-reports/step-listing-compare/ListingComparableDetails';
import {
  DELETE_PROPERTY_TRANSACTION_LISTING,
  GET_LINKED_LISTINGS,
  GET_TRANSACTION_PROPERTY,
  INSERT_PROPERTY_TRANSACTION_LISTINGS,
} from '@modules/transactions/transactionQueries';
import { useLocale } from '@shared/hooks/locale';
import { useSet } from '@shared/hooks/useSet';
import type { IntlLocale } from '@shared/LocaleContext';

interface TransactionComplianceProps {
  transactionId: string;
}

const SELECTION_LIMIT = 5;

// Transaction card component
const TransactionSection = ({
  transactionData,
}: {
  transactionData: GetTransactionPropertyQuery;
  locale: IntlLocale;
  countryCode: string;
}) => {
  const { t } = useLocale();

  const transaction = transactionData?.property_transactions_by_pk;

  if (!transaction) {
    return null;
  }

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Typography variant="h6" component="h3">
        {t('Transaction')}
      </Typography>
      <Divider />
      <TransactionCard transaction={transaction} />
    </Stack>
  );
};

// Custom hook for managing listing selection
interface ListingSelectionProps {
  transactionId: string;
  initialSelection: Array<number>;
  onComplete: () => void;
}

const useListingSelection = ({
  transactionId,
  initialSelection,
  onComplete,
}: ListingSelectionProps) => {
  const selection = useSet<string | number>([]);
  const hasInitialized = useRef(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const [insertTransactionListing] = useMutation(
    INSERT_PROPERTY_TRANSACTION_LISTINGS,
  );

  // Initialize selection once when data becomes available
  const initializeSelection = useCallback(() => {
    selection.clear();
    initialSelection.forEach(id => selection.add(id));
  }, [initialSelection, selection]);

  useEffect(() => {
    if (initialSelection.length > 0 && !hasInitialized.current) {
      initializeSelection();
      hasInitialized.current = true;
    }
  }, [initialSelection, initializeSelection]);

  const handleSelect = useCallback(
    (id: string | number) => {
      if (selection.has(id)) {
        return selection.delete(id);
      }
      if (selection.size < SELECTION_LIMIT) {
        return selection.add(id);
      }
    },
    [selection],
  );

  const handleUpdateSelection = useCallback(() => {
    setIsUpdating(true);
    const selectedIds = Array.from(selection.values());

    insertTransactionListing({
      variables: {
        objects: selectedIds.map(id => ({
          property_transaction_id: transactionId,
          listing_id: Number(id),
        })),
      },
      //   update: cache => {
      // Clear existing listings from cache
      // cache.modify({
      //   id: cache.identify({
      //     __typename: 'property_transactions',
      //     id: transactionId,
      //   }),
      //   fields: {
      //     property_transactions_listings() {
      //       return [];
      //     },
      //   },
      // });
      // // Add selected listings to cache
      // cache.modify({
      //   id: cache.identify({
      //     __typename: 'property_transactions',
      //     id: transactionId,
      //   }),
      //   fields: {
      //     property_transactions_listings() {
      //       return selectedIds.map(id => ({
      //         __typename: 'property_transactions_listings',
      //         listing_id: Number(id),
      //         property_transaction_id: transactionId,
      //       }));
      //     },
      //   },
      // });
      //   },
    })
      .then(onComplete)
      .finally(() => setIsUpdating(false));
  }, [insertTransactionListing, onComplete, selection, transactionId]);

  return {
    selection,
    isUpdating,
    handleSelect,
    handleUpdateSelection,
    initializeSelection,
    resetInitialization: () => {
      hasInitialized.current = false;
    },
  };
};

// Helper function to build query params for listing search
const buildListingSearchParams = (
  transaction: GetTransactionPropertyQuery['property_transactions_by_pk'],
  allPropertyTypes?: GetPropertyTypesQuery['property_types'],
): Ch_Cma_Listings_Distance_Bool_Exp => {
  if (!transaction) {
    return {};
  }

  const property = transaction.property;

  const whereParam: Ch_Cma_Listings_Distance_Bool_Exp = {
    listing: {
      is_online: { _eq: false },
    },
  };

  // Get property types based on main_type
  const mainType = allPropertyTypes?.find(
    type => type.name === property.property_type?.name,
  )?.main_type;

  // @ts-ignore: Ignore type incompatibility due to null vs undefined issues
  const propertyTypes = (
    allPropertyTypes
      ?.filter(type => type.main_type === mainType)
      ?.map(type => type.name ?? '') || []
  ).filter(Boolean);

  // Add property type filter if available
  if (propertyTypes.length > 0 && whereParam.listing) {
    whereParam.listing.property_types = {
      name: { _in: propertyTypes },
    };
  }

  // Add location filter if available
  if (property.locality && property.locality_place?.id && whereParam.listing) {
    whereParam.listing.places = {
      id: { _in: [property.locality_place.id] },
    };
  }

  // Add rooms filter if available
  if (property.number_of_rooms && whereParam.listing) {
    whereParam.listing.number_of_rooms = {
      _gte: Math.max(1, Math.floor(property.number_of_rooms) - 1),
      _lte: Math.floor(property.number_of_rooms) + 1,
    };
  }

  // Filter by agency if available
  const agencyFilters = [];

  if (transaction?.seller_broker_team?.listings_agency_names?.length) {
    agencyFilters.push({
      agency_name: {
        _in: transaction.seller_broker_team.listings_agency_names,
      },
    });
  }

  if (transaction?.seller_broker_team?.name) {
    agencyFilters.push({
      agency_name: {
        _ilike: `%${transaction.seller_broker_team.name}%`,
      },
    });
  }

  if (agencyFilters.length > 0 && whereParam.listing) {
    whereParam.listing._and = [{ _or: agencyFilters }];
  }

  return whereParam;
};

// Selection drawer header component
interface SelectionDrawerHeaderProps {
  onClose: () => void;
  selection: ReturnType<typeof useSet<string | number>>;
  onClear: () => void;
  onUpdate: () => void;
  isUpdating: boolean;
}

const SelectionDrawerHeader = ({
  onClose,
  selection,
  onClear,
  onUpdate,
  isUpdating,
}: SelectionDrawerHeaderProps) => {
  const { t } = useLocale();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <AppBar color="inherit" elevation={0} position="sticky">
      <Toolbar
        sx={{
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          borderBottom: `1px solid ${(theme.vars ?? theme).palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexGrow: 1,
          }}
        >
          <IconButton edge="start" color="inherit" onClick={onClose}>
            <XIcon size={20} />
          </IconButton>
          <Box
            sx={{
              ...theme.typography.h6,
              fontWeight: theme.text.font.bold,
              flexGrow: { xs: 1, sm: 0 },
            }}
          >
            {t('Listings')}
          </Box>
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <Button
              variant="text"
              disabled={selection.size === 0}
              onClick={onClear}
            >
              {t('Clear')}
            </Button>

            <LoadingButton
              variant="contained"
              color="primary"
              loading={isUpdating}
              loadingPosition="start"
              startIcon={
                <Box
                  sx={{
                    borderRadius: '50px',
                    fontSize: '12px !important',
                    padding: '0 8px',
                    backgroundColor:
                      selection.size === SELECTION_LIMIT
                        ? theme.palette.error.dark
                        : (theme.vars ?? theme).palette.primary.dark,
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '40px',
                  }}
                >
                  {isUpdating ? null : `${selection.size}/${SELECTION_LIMIT}`}
                </Box>
              }
              onClick={onUpdate}
            >
              <Typography variant="button" noWrap>
                {isMobile ? t('Save') : t('Update Selection')}
              </Typography>
            </LoadingButton>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

// Linked listings section component
const LinkedListingsSection = ({
  transactionData,
  transactionId,
}: {
  transactionData: GetTransactionPropertyQuery;
  transactionId: string;
}) => {
  const { t } = useLocale();
  const { countryCode } = useLocale();
  const theme = useTheme();
  const [deletingListingIds, setDeletingListingIds] = useState<Set<number>>(
    new Set(),
  );

  const [searchParams, setSearchParams] = useScopedSearchParams('comparables');
  const manualSelectionOpen = searchParams.get('compareDrawer') === 'open';

  // Extract data from transaction
  const property = transactionData?.property_transactions_by_pk?.property;
  const transaction = transactionData?.property_transactions_by_pk;

  // Get initial listing IDs
  const initialSelection = useMemo(
    () =>
      transactionData?.property_transactions_by_pk?.property_transactions_listings?.map(
        l => l.listing_id,
      ) || [],
    [transactionData],
  );

  // Handle closing the manual selection drawer
  const handleCloseManualSelection = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Fetch property types for filtering
  const { data: propertyTypesData } = useQuery(GET_PROPERTY_TYPES);
  const allPropertyTypes = propertyTypesData?.property_types;

  const [deletePropertyTransactionListing] = useMutation(
    DELETE_PROPERTY_TRANSACTION_LISTING,
  );

  // Fetch the linked listings
  const { data: listingsData, refetch: refetchListings } = useQuery(
    GET_LINKED_LISTINGS,
    {
      variables: {
        ids: initialSelection,
        ch: countryCode === 'CH',
        fr: countryCode === 'FR',
        es: countryCode === 'ES',
        it: countryCode === 'IT',
      },
      skip: !initialSelection.length,
      context: {
        clientName: 'scrapers',
      },
    },
  );

  // Get listings from appropriate country collection
  const listings = useMemo(
    () =>
      listingsData?.ch_cma_listings ??
      listingsData?.es_cma_listings ??
      listingsData?.fr_cma_listings ??
      listingsData?.it_cma_listings ??
      [],
    [listingsData],
  );

  // Use custom hook for selection management
  const {
    selection,
    isUpdating,
    handleSelect,
    handleUpdateSelection,
    initializeSelection,
    resetInitialization,
  } = useListingSelection({
    transactionId,
    initialSelection,
    onComplete: () => {
      handleCloseManualSelection();
      refetchListings();
    },
  });

  // Reset initialization when drawer closes
  useEffect(() => {
    if (!manualSelectionOpen) {
      resetInitialization();
    }
  }, [manualSelectionOpen, resetInitialization]);

  // Handle opening the manual selection drawer
  const handleOpenManualSelection = useCallback(() => {
    // Create new search params
    const newParams = new URLSearchParams(searchParams);
    newParams.set('compareDrawer', 'open');

    // Reinitialize selection
    initializeSelection();

    // Build search params for listings
    const whereParam = buildListingSearchParams(transaction, allPropertyTypes);

    // Set search params
    if (Object.keys(whereParam).length > 0) {
      newParams.set('where', JSON.stringify(whereParam));
    }

    setSearchParams(newParams);
  }, [
    transaction,
    allPropertyTypes,
    searchParams,
    setSearchParams,
    initializeSelection,
  ]);

  return (
    <Stack spacing={2} sx={{ maxHeight: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">{t('Linked Listings')}</Typography>
        <Button
          startIcon={<MousePointerClickIcon />}
          onClick={handleOpenManualSelection}
          variant="contained"
          size="small"
        >
          {t('Link Manually')}
        </Button>
      </Box>
      <Divider />
      <Stack spacing={2}>
        {listings.length > 0 ? (
          listings.map(listing => (
            <Card
              key={listing.id}
              sx={{ display: 'flex', position: 'relative' }}
            >
              <CmaScraperCard
                cmaListingDistance={{
                  id: listing.id.toString(),
                  listing,
                  // we don't need to show the distance here
                  distance_in_meters: 0,
                }}
                variant="horizontal"
                showAgencyName={true}
                showDaysOnMarket={false}
                showContactName={true}
                showOfflineSince={true}
              />
              <div
                css={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  padding: 0,
                  display: 'inline-flex',
                }}
              >
                <IconButton
                  size="small"
                  onClick={async evt => {
                    evt.stopPropagation();
                    evt.preventDefault();
                    setDeletingListingIds(prev =>
                      new Set(prev).add(listing.id),
                    );
                    try {
                      await deletePropertyTransactionListing({
                        variables: {
                          id: transactionId,
                          listingId: listing.id,
                        },
                        update: cache => {
                          // First identify the specific listing object
                          const objectId = cache.identify({
                            __typename: 'property_transactions_listings',
                            property_transaction_id: transactionId,
                            listing_id: listing.id,
                          });

                          // Evict the specific listing object
                          cache.evict({
                            id: objectId,
                          });

                          // Update the parent transaction to remove this listing from its list
                          cache.modify({
                            id: cache.identify({
                              __typename: 'property_transactions',
                              id: transactionId,
                            }),
                            fields: {
                              property_transactions_listings(
                                existingListings = [],
                                { readField },
                              ) {
                                return existingListings.filter(
                                  (listingRef: any) =>
                                    readField('listing_id', listingRef) !==
                                    listing.id,
                                );
                              },
                            },
                          });
                        },
                      });
                    } finally {
                      setDeletingListingIds(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(listing.id);
                        return newSet;
                      });
                    }
                  }}
                  onMouseDown={evt => {
                    evt.stopPropagation();
                  }}
                  disabled={deletingListingIds.has(listing.id)}
                >
                  {deletingListingIds.has(listing.id) ? (
                    <CircularProgress
                      sx={{
                        color: (theme.vars ?? theme).palette.text.secondary,
                        opacity: 0.5,
                      }}
                      size={20}
                    />
                  ) : (
                    <XIcon size={20} />
                  )}
                </IconButton>
              </div>
            </Card>
          ))
        ) : (
          <Typography
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            {t('No listings linked yet')}
          </Typography>
        )}
      </Stack>
      <Drawer
        open={manualSelectionOpen}
        anchor="right"
        onClose={handleCloseManualSelection}
        slotProps={{
          paper: {
            sx: {
              width: [
                '100%',
                '100%',
                'calc((100% - var(--app-sidebar-width, 0px)) / 2)',
                'calc((100% - var(--app-sidebar-width, 0px)) / 2)',
              ],
              maxWidth: '100%',
              maxHeight: '100vh',
              position: 'fixed',
              right: 0,
              top: 0,
              zIndex: theme.zIndex.drawer,
            },
          },
          backdrop: {
            sx: { bgcolor: 'rgba(0, 0, 0, 0.2)' },
          },
        }}
        sx={{ zIndex: theme.zIndex.drawer + 1 }}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <SelectionDrawerHeader
            onClose={handleCloseManualSelection}
            selection={selection}
            onClear={selection.clear}
            onUpdate={handleUpdateSelection}
            isUpdating={isUpdating}
          />

          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {property && (
              <ComparableListings
                selection={selection}
                onSelect={handleSelect}
                lat={property.lat ?? null}
                lng={property.lng ?? null}
                stateId={property.state_place?.id ?? null}
                queryParamsScope="comparables"
                showAgencyName={true}
                showContactName={true}
                variant="horizontal"
                gridItemProps={{ xs: 12 }}
                isDrawer={true}
              />
            )}
          </Box>
        </Box>
      </Drawer>
    </Stack>
  );
};

export const TransactionCompliance: React.FC<TransactionComplianceProps> = ({
  transactionId,
}) => {
  const { countryCode, locale } = useLocale();
  const { data: transactionData } = useQuery(GET_TRANSACTION_PROPERTY, {
    variables: { id: transactionId },
  });

  if (!transactionData) {
    return null;
  }

  return (
    <PropertyTypesProvider>
      <Stack
        direction={{
          xs: 'column',
          md: 'row',
        }}
        spacing={2}
        sx={{
          m: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
          }}
        >
          <TransactionSection
            transactionData={transactionData}
            locale={locale}
            countryCode={countryCode}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
          }}
        >
          <LinkedListingsSection
            transactionData={transactionData}
            transactionId={transactionId}
          />
        </Box>
        <Routes>
          <Route
            path=":encodedListingId"
            element={<ListingComparableDetails />}
          />
        </Routes>
      </Stack>
    </PropertyTypesProvider>
  );
};

export default TransactionCompliance;
